import { api } from "./api";

export interface VehicleOpsMetrics {
  active_vehicles: number;
  in_repair: number;
  pending_approval: number;
  license_warnings: number;
  month_maintenance_cents: number;
  month_fuel_cents: number;
  total_mileage_km: number;
}

export interface LicenseAlert {
  vehicle_number: string;
  license_type: string;
  expiry_date: string;
  days_to_expiry: number;
  level: "yellow" | "red" | "expired";
}

export interface FleetHealthItem {
  vehicle_number: string;
  model: string;
  status: string;
  ownership_type: string;
  mileage_km: number | null;
  health_score: number;
  alert: LicenseAlert | null;
}

export interface RecentMaintenance {
  order_number: string;
  vehicle_number: string;
  maintenance_type: string;
  status: string;
  cost_cents: number;
  vendor_name: string | null;
  created_at: string | null;
}

export interface VehicleOpsOverview {
  metrics: VehicleOpsMetrics;
  license_alerts: LicenseAlert[];
  fleet_health: FleetHealthItem[];
  recent_maintenance: RecentMaintenance[];
  expense_summary: {
    maintenance_cents: number;
    fuel_cents: number;
    insurance_cents: number;
    fine_cents: number;
    total_cents: number;
  };
}

export interface MaintenanceOrder {
  id: string;
  vehicle_id: string;
  order_number: string;
  maintenance_type: string;
  status: string; // pending / in_progress / completed / rejected / cancelled
  description: string | null;
  estimated_cost_cents: number | null;
  actual_cost_cents: number | null;
  scheduled_at: string | null;
  approved_at: string | null;
  vendor_name: string | null;
  created_at: string;
}

export interface MaintenanceCreateBody {
  vehicle_id: string;
  maintenance_type: string;
  description?: string;
  estimated_cost_cents?: number;
  scheduled_at?: string;
  vendor_name?: string;
}

export interface VehicleExpense {
  id: string;
  receipt_number: string;
  category: string;
  amount_cents: number;
  receipt_date: string;
  related_vehicle_id: string | null;
  related_maintenance_order_id?: string | null;
  submitter_user_id?: string | null;
  status: string;
  escalation_reason?: string | null;
  description: string | null;
}
export interface ExpenseUploadBody {
  category: string;
  amount_cents: number;
  receipt_date: string;
  related_vehicle_id?: string;
  related_maintenance_order_id?: string;
  description?: string;
  receipt_image_url?: string;
}
export interface FleetLicense {
  id: string;
  vehicle_id: string;
  license_type: string;
  license_number: string;
  expires_at: string;
  days_to_expiry: number;
  warning_level: string; // green / yellow / red / expired
}

export const vehicleOpsApi = {
  overview: () => api<VehicleOpsOverview>("/api/v1/vehicle-ops/overview"),
  listExpenses: () => api<VehicleExpense[]>("/api/v1/expense-receipts?origin=vehicle_ops&limit=200"),
  // 财务·费用报销查询（origin: vehicle_ops 车务费用 / driver_reimbursement 司导报销 / all）
  listExpensesFiltered: (p?: { origin?: string; status?: string }) => {
    const qs = new URLSearchParams();
    qs.set("origin", p?.origin ?? "all");
    if (p?.status) qs.set("status", p.status);
    qs.set("limit", "200");
    return api<VehicleExpense[]>(`/api/v1/expense-receipts?${qs.toString()}`);
  },
  uploadExpense: (body: ExpenseUploadBody) =>
    api<VehicleExpense>("/api/v1/expense-receipts", { method: "POST", body: JSON.stringify(body) }),
  approveExpense: (id: string) =>
    api<VehicleExpense>(`/api/v1/expense-receipts/${id}/approve`, { method: "PATCH" }),
  rejectExpense: (id: string, reason: string) =>
    api<VehicleExpense>(`/api/v1/expense-receipts/${id}/reject`, { method: "PATCH", body: JSON.stringify({ reason }) }),
  escalateExpense: (id: string, reason: string) =>  // 特批工单·财务升级老板
    api<VehicleExpense>(`/api/v1/expense-receipts/${id}/escalate`, { method: "PATCH", body: JSON.stringify({ reason }) }),
  listFleetLicenses: () => api<FleetLicense[]>("/api/v1/vehicle-licenses?limit=500"),
  listMaintenance: (params?: { status?: string; vehicle_id?: string }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.vehicle_id) qs.set("vehicle_id", params.vehicle_id);
    qs.set("limit", "200");   // 后端默认仅 50·拉满 200(上限)·KPI/tab 客户端算需全集·真分页待后端 stats 端点(backlog)
    return api<MaintenanceOrder[]>(`/api/v1/maintenance-orders?${qs.toString()}`);
  },
  createMaintenance: (body: MaintenanceCreateBody) =>
    api<MaintenanceOrder>("/api/v1/maintenance-orders", { method: "POST", body: JSON.stringify(body) }),
  approveMaintenance: (id: string) =>
    api<MaintenanceOrder>(`/api/v1/maintenance-orders/${id}/approve`, { method: "PATCH" }),
  rejectMaintenance: (id: string, reason: string) =>
    api<MaintenanceOrder>(`/api/v1/maintenance-orders/${id}/reject`, { method: "PATCH", body: JSON.stringify({ reason }) }),
  completeMaintenance: (id: string, actual_cost_cents: number) =>
    api<MaintenanceOrder>(`/api/v1/maintenance-orders/${id}/complete`, { method: "PATCH", body: JSON.stringify({ actual_cost_cents }) }),
};
